import useTheme from 'chat-ui/src/hooks/useTheme';
import { useEffect } from 'react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Outlet, useLocation } from 'react-router-dom';
import ImageViewer from 'chat-ui/src/components/Image/ImageViewer';
import useDashboardTitle from '@/hooks/useDashboardTitle';
import { ECompanyThemeName } from '@/enums';
import useAuthStore from '@/store/AuthStore';
import Routes from '@/router/routes';
import { LangProvider } from '@/context/LangProvider';

const faviconsList = {
  [ECompanyThemeName.DEFAULT]: '/favicon.ico',
  [ECompanyThemeName.EMPATHY]: '/faviconempathy.ico',
};

const themeNamesList = {
  [ECompanyThemeName.DEFAULT]: 'Tellet - Dashboard',
  [ECompanyThemeName.EMPATHY]: 'Empathy - Dashboard',
};

function ThemeWrapperDashboard() {
  const { changeTheme, themeName } = useTheme();
  const url = window.location.hostname;
  const parts = url.split('.');
  const nameFromUrl = parts[1];
  const { authToken } = useAuthStore();
  const location = useLocation();
  const tab_title = themeNamesList[themeName] || ECompanyThemeName.DEFAULT;
  const tab_favicon = faviconsList[themeName] || '/favicon.ico';

  // Pages that need toast notifications even when potentially authenticated
  const authPagesNeedingToasts = [
    Routes.verification_email_link_expired.path,
    Routes.reinvite_request_sent.path,
    Routes.organization_invitation_link_expired.path,
  ];

  const needsToastContainer =
    !authToken || authPagesNeedingToasts.some((path) => location.pathname.includes(path));

  function isValidThemeName(name: string): name is ECompanyThemeName {
    return Object.values(ECompanyThemeName).includes(name as ECompanyThemeName);
  }

  useEffect(() => {
    if (parts.length >= 3 && isValidThemeName(nameFromUrl)) {
      changeTheme(nameFromUrl);
    } else {
      // change theme for local testing here 👇
      changeTheme(ECompanyThemeName.DEFAULT);
    }
  }, [changeTheme, nameFromUrl, parts]);

  useDashboardTitle({ title: tab_title, favicon: tab_favicon });

  return (
    <>
      <LangProvider>
        <Outlet />
      </LangProvider>
      <ImageViewer />
      {/* Toast container - rendered for auth pages or when not authenticated */}
      {needsToastContainer && (
        <ToastContainer
          position="top-center"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          style={{
            zIndex: 99999999999,
            width: 'auto',
            minWidth: '340px',
            maxWidth: '520px',
            fontSize: '0.875rem',
          }}
        />
      )}
    </>
  );
}

export default ThemeWrapperDashboard;
